/**
 * Las conversaciones que COMPONEN un contador.  (FR-014)
 *
 * *Un número en el que no se puede entrar es un callejón sin salida.* Si el panel
 * le dice a un cliente "3 se quedaron a medias", tiene derecho a ver **cuáles** —
 * persona, teléfono, correo, en qué punto, y la conversación en vivo (solo lectura).
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router'

import { useAbrirHilo } from '../components/HilosFlotantes'
import { etiquetaPersonaContador } from '../lib/etiqueta-persona-contador'
import { api } from '../lib/api'
import { ETIQUETA_CONSEGUIDO_CORTA, TEXTO_CONSEGUIDO } from '../lib/conseguido'
import { cn } from '../lib/cn'
import { fechaHora } from '../lib/tiempo'
import {
    compararOrden,
    direccionInicial,
    textoVentanaLista,
    type ClaveOrden,
    type DireccionOrden,
} from '../lib/ventana-lista'

interface Item {
    conversationId: number
    outcome: string
    outcomeReason: string | null
    lastObservedAt: string
    chatwootUrl: string | null
    persona: string | null
    telefono: string | null
    email: string | null
    hiloBorrado?: boolean
    punto: string
    conseguido: 'reserva' | 'cancelacion' | 'modificacion' | null
    hilo: 'abierta' | 'cerrada' | null
    testigo: 'agendify' | 'equipo' | null
    ultimoClienteAt: string | null
    ventanaConocida: boolean
}

const REFRESCO_LISTA_MS = 15_000

const COLUMNAS: { clave: ClaveOrden; etiqueta: string }[] = [
    { clave: 'nombre', etiqueta: 'Nombre' },
    { clave: 'telefono', etiqueta: 'Teléfono' },
    { clave: 'correo', etiqueta: 'Correo' },
    { clave: 'estado', etiqueta: 'Estado' },
    { clave: 'ventana', etiqueta: 'Ventana (24 h)' },
    { clave: 'testigo', etiqueta: 'Testigo' },
    { clave: 'actividad', etiqueta: 'Última actividad' },
]

function textoTestigo(h: Item['hilo'], t: Item['testigo']): string {
    if (t === 'agendify') return 'Agendify'
    if (t === 'equipo') return 'Equipo'
    if (h === 'cerrada') return '—'
    return 'No se sabe'
}

function textoEstado(i: Item): string {
    if (i.conseguido) return ETIQUETA_CONSEGUIDO_CORTA[i.conseguido]
    return i.punto
}

function valorOrden(i: Item, clave: ClaveOrden): string | null {
    if (clave === 'nombre') return etiquetaPersonaContador(i)
    if (clave === 'telefono') return i.telefono
    if (clave === 'correo') return i.email
    if (clave === 'estado') return textoEstado(i)
    if (clave === 'ventana') return i.ventanaConocida ? i.ultimoClienteAt : null
    if (clave === 'testigo') return textoTestigo(i.hilo, i.testigo)
    return i.lastObservedAt
}

const TITULOS: Record<string, string> = {
    total: 'Todas las conversaciones',
    enCurso: 'En curso',
    cerradasBien: 'Acabaron en cita',
    finalizadas: 'Finalizadas',
    canceladas: 'Cancelaron',
    escaladas: 'Pasaron a un humano',
    informativas: 'Consultaron',
    enEspera: 'En espera',
    sinEncaje: 'No encajó',
    colgadas: 'Se quedaron a medias',
}

export function ContadorDetalle() {
    const { accountId, nombre } = useParams()
    const [params, setParams] = useSearchParams()
    const periodo = params.get('periodo') ?? 'hoy'
    const incluirTests = params.get('incluirTests') === '1'
    const convParam = params.get('conv')
    const abrirHilo = useAbrirHilo()
    const [orden, setOrden] = useState<{ clave: ClaveOrden; dir: DireccionOrden }>({
        clave: 'actividad',
        dir: 'desc',
    })
    const [ahora, setAhora] = useState(() => Date.now())

    const abrir = (id: number) => {
        if (accountId) abrirHilo(Number(accountId), id)
        const siguiente = new URLSearchParams(params)
        siguiente.set('conv', String(id))
        setParams(siguiente, { replace: true })
    }
    useEffect(() => {
        if (!accountId || convParam === null) return
        const id = Number(convParam)
        if (!Number.isInteger(id)) return
        abrirHilo(Number(accountId), id)
    }, [accountId, convParam, abrirHilo])

    const { data, isLoading, refetch } = useQuery<{ items: Item[] }>({
        queryKey: ['counter', accountId, nombre, periodo, incluirTests],
        queryFn: () =>
            api.get(
                `/business/${accountId}/counter/${nombre}?periodo=${periodo}${incluirTests ? '&incluirTests=1' : ''}`,
            ),
        enabled: Boolean(accountId && nombre),
    })
    useEffect(() => {
        const id = window.setInterval(() => {
            if (document.hidden) return
            void refetch()
        }, REFRESCO_LISTA_MS)
        return () => window.clearInterval(id)
    }, [refetch])
    useEffect(() => {
        const id = window.setInterval(() => setAhora(Date.now()), 1000)
        return () => window.clearInterval(id)
    }, [])
    const ordenar = (clave: ClaveOrden) => {
        setOrden((actual) =>
            actual.clave === clave
                ? { clave, dir: actual.dir === 'asc' ? 'desc' : 'asc' }
                : { clave, dir: direccionInicial(clave) },
        )
    }
    // 025 · FR-003: un hilo que el canal ya no tiene no se enseña.
    const items = useMemo(() => {
        const vivos = (data?.items ?? []).filter((i) => !i.hiloBorrado)
        return [...vivos].sort((a, b) => compararOrden(valorOrden(a, orden.clave), valorOrden(b, orden.clave), orden.dir))
    }, [data?.items, orden])

    return (
        <div className="space-y-5 font-sans">
            <Link to={`/business/${accountId}`} className="text-sm text-neutral-500 hover:text-neutral-300">
                ← Volver
            </Link>

            <h2 className="text-2xl font-semibold tracking-tight text-neutral-50">
                {TITULOS[nombre ?? ''] ?? nombre}
                {data && (
                    <span className="ml-2 text-base font-normal text-neutral-500">
                        {items.length === 1 ? '1 conversación' : `${items.length} conversaciones`}
                    </span>
                )}
            </h2>

            {isLoading && <p className="text-sm text-neutral-500">Cargando…</p>}

            {data && items.length === 0 && (
                <p className="text-sm text-neutral-500">Ninguna conversación en este grupo.</p>
            )}

            {data && items.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-neutral-800">
                    <div className="flex flex-wrap gap-1.5 border-b border-neutral-800 px-3 py-2 md:hidden">
                        {COLUMNAS.map((c) => (
                            <BotonOrden
                                key={c.clave}
                                etiqueta={c.etiqueta}
                                activo={orden.clave === c.clave}
                                dir={orden.dir}
                                onClick={() => ordenar(c.clave)}
                            />
                        ))}
                    </div>
                    <table className="tabla-carta w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-neutral-800 text-left text-xs text-neutral-500">
                                {COLUMNAS.map((c) => (
                                    <th
                                        key={c.clave}
                                        className="px-3 py-2 font-medium"
                                        aria-sort={
                                            orden.clave === c.clave
                                                ? orden.dir === 'asc'
                                                    ? 'ascending'
                                                    : 'descending'
                                                : 'none'
                                        }
                                    >
                                        <BotonOrden
                                            etiqueta={c.etiqueta}
                                            activo={orden.clave === c.clave}
                                            dir={orden.dir}
                                            onClick={() => ordenar(c.clave)}
                                        />
                                    </th>
                                ))}
                                <th className="px-3 py-2 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((i) => (
                                <tr key={i.conversationId} className="border-b border-neutral-900 last:border-b-0 hover:bg-neutral-900/40">
                                    <td className="px-3 py-2" data-label="Nombre">
                                        <button
                                            type="button"
                                            onClick={() => abrir(i.conversationId)}
                                            className={cn(
                                                'text-left font-medium hover:underline',
                                                i.hiloBorrado ? 'text-neutral-500' : 'text-neutral-100',
                                            )}
                                        >
                                            {etiquetaPersonaContador(i)}
                                        </button>
                                    </td>
                                    <td className="px-3 py-2 tabular-nums text-neutral-300" data-label="Teléfono">
                                        {i.telefono ?? <span className="text-neutral-600">—</span>}
                                    </td>
                                    <td className="px-3 py-2 text-neutral-400" data-label="Correo">
                                        {i.email ?? <span className="text-neutral-600">—</span>}
                                    </td>
                                    <td className="px-3 py-2" data-label="Estado">
                                        {i.conseguido ? (
                                            <span className={cn('font-medium', TEXTO_CONSEGUIDO[i.conseguido])}>
                                                {ETIQUETA_CONSEGUIDO_CORTA[i.conseguido]}
                                            </span>
                                        ) : (
                                            <span className="text-neutral-400">{i.punto}</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 tabular-nums" data-label="Ventana (24 h)">
                                        <CuentaVentana iso={i.ultimoClienteAt} conocida={i.ventanaConocida} ahora={ahora} />
                                    </td>
                                    <td className="px-3 py-2 text-neutral-300" data-label="Testigo">
                                        {textoTestigo(i.hilo, i.testigo)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-neutral-500" data-label="Última actividad">
                                        {fechaHora(i.lastObservedAt)}
                                    </td>
                                    <td className="px-3 py-2 text-right" data-label="">
                                        {i.chatwootUrl && (
                                            <a
                                                href={i.chatwootUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-sky-400 hover:text-sky-300"
                                            >
                                                Chatwoot ↗
                                            </a>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}

function BotonOrden({
    etiqueta,
    activo,
    dir,
    onClick,
}: {
    etiqueta: string
    activo: boolean
    dir: DireccionOrden
    onClick: () => void
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'inline-flex items-center gap-1 text-left font-medium hover:text-neutral-200',
                activo ? 'text-neutral-200' : 'text-neutral-500',
            )}
        >
            {etiqueta}
            {activo && <span aria-hidden="true">{dir === 'asc' ? '↑' : '↓'}</span>}
        </button>
    )
}

function CuentaVentana({
    iso,
    conocida,
    ahora,
}: {
    iso: string | null
    conocida: boolean
    ahora: number
}) {
    const v = textoVentanaLista(iso, conocida, ahora)
    return (
        <span
            className={cn(
                v.tono === 'abierta' && 'text-emerald-300',
                v.tono === 'cerrada' && 'text-amber-300',
                v.tono === 'desconocida' && 'text-neutral-500',
            )}
        >
            {v.texto}
        </span>
    )
}
